import React, { lazy } from "react";

import { Outlet, RouteObject } from "react-router";

import { DashboardFiltersContextProvider } from "@dashboard/FiltersContext";
import { SeasonCompareContextProvider } from "@dashboard/SeasonCompareContext";
import SupportPage from "@pages/support";
import ProOrganizationOnly from "@shared/components/guards/pro-organization-only";

import AppLayout from "./pages/_app";

const NotFound = lazy(() => import("./pages/404"));
const Index = lazy(() => import("./pages/index"));

// AUTH
const AuthLayout = lazy(() => import("./pages/(auth)/_layout"));
const Login = lazy(() => import("./pages/(auth)/login"));
const Signup = lazy(() => import("./pages/(auth)/signup"));
const ResetPassword = lazy(() => import("./pages/(auth)/reset-password"));

// FRONTOFFICE
const FrontofficeLayout = lazy(() => import("./pages/(frontoffice)/_layout"));
const BookingMetrics = lazy(
  () => import("./pages/(frontoffice)/dashboard/booking-metrics"),
);
const ReportMetrics = lazy(
  () => import("./pages/(frontoffice)/dashboard/report-metrics"),
);
const Invitations = lazy(() => import("./pages/(frontoffice)/invitations"));
const ErrorPage = lazy(() => import("./pages/(frontoffice)/error"));
const MyProfileLayout = lazy(
  () => import("./pages/(frontoffice)/my-profile/_layout"),
);
const PersonalInfosPage = lazy(
  () => import("./pages/(frontoffice)/my-profile/personal-infos"),
);
const VirtualToolsPage = lazy(
  () => import("./pages/(frontoffice)/my-profile/virtual-tools"),
);
const SettingsPage = lazy(
  () => import("./pages/(frontoffice)/my-profile/settings"),
);
const MyProfileLoginPage = lazy(
  () => import("./pages/(frontoffice)/my-profile/login"),
);
const IntegrationsPage = lazy(
  () => import("./pages/(frontoffice)/my-profile/integrations"),
);

const OrganizationInformation = lazy(
  () => import("./pages/(frontoffice)/organization-profile/information"),
);
const OrganizationPortfolios = lazy(
  () => import("./pages/(frontoffice)/organization-profile/portfolios"),
);
const OrganizationCollections = lazy(
  () => import("./pages/(frontoffice)/organization-profile/collections"),
);
const OrganizationLayout = lazy(
  () => import("./pages/(frontoffice)/organization-profile/_layout"),
);
const RepresentativesList = lazy(
  () => import("./pages/(frontoffice)/representatives/list"),
);
const RepresentativesArchived = lazy(
  () => import("./pages/(frontoffice)/representatives/archived"),
);
const RepresentativeDetail = lazy(
  () => import("./pages/(frontoffice)/representatives/[representativeId]"),
);
const CalendarIndex = lazy(
  () => import("./pages/(frontoffice)/calendar/index"),
);
const CalendarDaily = lazy(
  () => import("./pages/(frontoffice)/calendar/[season]/[year]/daily/[day]"),
);
const CalendarArchive = lazy(
  () =>
    import(
      "./pages/(frontoffice)/calendar/[season]/[year]/daily/archive/[day]"
    ),
);
const ShowroomIndex = lazy(
  () => import("./pages/(frontoffice)/showroom/index"),
);
const ShowroomDetail = lazy(
  () => import("./pages/(frontoffice)/showroom/[showroomId]/index"),
);
const CrmLayout = lazy(() => import("./pages/(frontoffice)/crm/_layout"));
const AccountsIndex = lazy(
  () => import("./pages/(frontoffice)/crm/accounts/index"),
);
const AccountDetail = lazy(
  () => import("./pages/(frontoffice)/crm/accounts/[accountId]/index"),
);
const MeetingReport = lazy(
  () =>
    import(
      "./pages/(frontoffice)/crm/accounts/[accountId]/meeting-report/index"
    ),
);
const MeetingReportDetail = lazy(
  () =>
    import(
      "./pages/(frontoffice)/crm/accounts/[accountId]/meeting-report/[appointmentId]"
    ),
);
const CrmContacts = lazy(() => import("./pages/(frontoffice)/crm/contacts"));
const CrmCompetitors = lazy(
  () => import("./pages/(frontoffice)/crm/competitors"),
);
const CrmArchivedAccounts = lazy(
  () => import("./pages/(frontoffice)/crm/archived-accounts"),
);
const CrmArchivedContacts = lazy(
  () => import("./pages/(frontoffice)/crm/archived-contacts"),
);

// BOOKING
const BookingLayout = lazy(() => import("./pages/booking/_layout"));
const InvitationBooking = lazy(
  () => import("./pages/booking/[invitationId]/index"),
);
const BookAppointmentLayout = lazy(
  () => import("./pages/booking/[invitationId]/book-appointment/_layout"),
);
const BookAppointment = lazy(
  () => import("./pages/booking/[invitationId]/book-appointment/index"),
);
const EditAppointment = lazy(
  () =>
    import(
      "./pages/booking/[invitationId]/book-appointment/edit/[appointmentId]"
    ),
);

// BACKOFFICE
const BackofficeLayout = lazy(() => import("./pages/backoffice/_layout"));
const ListOrganizations = lazy(
  () => import("./pages/backoffice/list-organizations"),
);
const ListBuyers = lazy(() => import("./pages/backoffice/list-buyers"));
const OnboardOrganization = lazy(
  () => import("./pages/backoffice/onboard-organization/index"),
);
const OrgInfo = lazy(
  () =>
    import(
      "./pages/backoffice/onboard-organization/[organizationId]/information"
    ),
);
const RepsUpload = lazy(
  () =>
    import(
      "./pages/backoffice/onboard-organization/[organizationId]/representatives-upload"
    ),
);
const AccountsUpload = lazy(
  () =>
    import(
      "./pages/backoffice/onboard-organization/[organizationId]/accounts-upload"
    ),
);
const ContactsUpload = lazy(
  () =>
    import(
      "./pages/backoffice/onboard-organization/[organizationId]/contacts-upload"
    ),
);

// ONBOARDING
const OnboardingLayout = lazy(() => import("./pages/onboarding/_layout"));
const UploadCRM = lazy(() => import("./pages/onboarding/upload-crm"));
const OnboardingInformation = lazy(
  () => import("./pages/onboarding/information"),
);
const CreateOrganization = lazy(
  () => import("./pages/onboarding/create-organization"),
);
const CreateShowroom = lazy(() => import("./pages/onboarding/create-showroom"));
const ShowroomStructure = lazy(
  () => import("./pages/onboarding/showroom-structure"),
);
const AccountPortfolio = lazy(
  () => import("./pages/onboarding/account-portfolio"),
);

// BUYER + MISC
const BuyerLogin = lazy(() => import("./pages/buyer-login"));
const BuyerLayout = lazy(() => import("./pages/buyer/_layout"));
const BuyerCampaigns = lazy(() => import("./pages/buyer/campaigns"));
const BuyerShowroomHeatmap = lazy(() => import("./pages/buyer/heatmap"));

// CRUISING
const CruisingLayout = lazy(() => import("./pages/cruising/_layout"));
const CruisingIndex = lazy(() => import("./pages/cruising/index"));
const CruisingOld = lazy(() => import("./pages/cruising/old"));
const PlanMyTrip = lazy(() => import("./pages/cruising/plan-my-trip"));

// SIGNUP
const SignupBrand = lazy(() => import("./pages/signup-brand/[brandId]/index"));
const SignupAgency = lazy(
  () => import("./pages/signup-agency/[agencyId]/index"),
);

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppLayout />,
    children: [
      { index: true, element: <Index /> },

      // AUTH INTERFACE
      { path: "buyer-login", element: <BuyerLogin /> },
      { path: "signup-brand/:brandId", element: <SignupBrand /> },
      { path: "signup-agency/:agencyId", element: <SignupAgency /> },
      {
        element: <AuthLayout />,
        children: [
          { path: "login", element: <Login /> },
          { path: "signup", element: <Signup /> },
          { path: "reset-password", element: <ResetPassword /> },
        ],
      },

      // BRAND / AGENCY INTERFACE
      {
        element: <FrontofficeLayout />,
        children: [
          {
            path: "dashboard",
            element: (
              <DashboardFiltersContextProvider name="dashboard-filters">
                <SeasonCompareContextProvider name="dashboard-seasons-to-compare">
                  <Outlet />
                </SeasonCompareContextProvider>
              </DashboardFiltersContextProvider>
            ),
            children: [
              {
                index: true,
                element: <BookingMetrics />,
              },
              {
                path: "report-metrics",
                element: <ReportMetrics />,
              },
              {
                path: "booking-metrics",
                element: <BookingMetrics />,
              },
            ],
          },
          {
            path: "invitations",
            element: <Invitations />,
          },
          { path: "error", element: <ErrorPage /> },
          {
            path: "my-profile",
            element: <MyProfileLayout />,
            children: [
              {
                element: <PersonalInfosPage />,
                index: true,
              },
              { path: "personal-info", element: <PersonalInfosPage /> },
              { path: "login", element: <MyProfileLoginPage /> },
              { path: "integrations", element: <IntegrationsPage /> },
              { path: "virtual-tools", element: <VirtualToolsPage /> },
              { path: "settings", element: <SettingsPage /> },
            ],
          },

          {
            path: "organization-profile",
            element: <OrganizationLayout />,
            children: [
              { path: "information", element: <OrganizationInformation /> },
              { path: "portfolios", element: <OrganizationPortfolios /> },
              { path: "collections", element: <OrganizationCollections /> },
            ],
          },

          {
            path: "representatives",
            children: [
              { path: "list", element: <RepresentativesList /> },
              { path: "archived", element: <RepresentativesArchived /> },
              { path: ":representativeId", element: <RepresentativeDetail /> },
            ],
          },

          {
            path: "calendar",
            element: (
              <ProOrganizationOnly>
                <Outlet />
              </ProOrganizationOnly>
            ),
            children: [
              { index: true, element: <CalendarIndex /> },
              { path: ":season/:year/daily/:day", element: <CalendarDaily /> },
              {
                path: ":season/:year/daily/archive/:day",
                element: <CalendarArchive />,
              },
            ],
          },

          {
            path: "showroom",
            children: [
              { index: true, element: <ShowroomIndex /> },
              { path: ":showroomId", element: <ShowroomDetail /> },
            ],
          },

          {
            path: "crm",
            element: <CrmLayout />,
            children: [
              { path: "accounts", element: <AccountsIndex /> },
              { path: "accounts/:accountId", element: <AccountDetail /> },
              {
                path: "accounts/:accountId/meeting-report",
                element: <MeetingReport />,
              },
              {
                path: "accounts/:accountId/meeting-report/:appointmentId",
                element: <MeetingReportDetail />,
              },
              { path: "contacts", element: <CrmContacts /> },
              { path: "competitors", element: <CrmCompetitors /> },
              { path: "archived-accounts", element: <CrmArchivedAccounts /> },
              { path: "archived-contacts", element: <CrmArchivedContacts /> },
            ],
          },
        ],
      },

      // BUYER JOURNEY
      {
        path: "booking",
        element: <BookingLayout />,
        children: [
          {
            path: ":invitationId",
            children: [
              {
                index: true,
                element: <InvitationBooking />,
              },
              {
                path: "book-appointment",
                element: <BookAppointmentLayout />,
                children: [
                  { index: true, element: <BookAppointment /> },
                  { path: "edit/:appointmentId", element: <EditAppointment /> },
                ],
              },
            ],
          },
        ],
      },

      // BACKOFFICE
      {
        path: "backoffice",
        element: <BackofficeLayout />,
        children: [
          { path: "list-organizations", element: <ListOrganizations /> },
          { path: "list-buyers", element: <ListBuyers /> },
          { path: "onboard-organization", element: <OnboardOrganization /> },
          {
            path: "onboard-organization/:organizationId/information",
            element: <OrgInfo />,
          },
          {
            path: "onboard-organization/:organizationId/representatives-upload",
            element: <RepsUpload />,
          },
          {
            path: "onboard-organization/:organizationId/accounts-upload",
            element: <AccountsUpload />,
          },
          {
            path: "onboard-organization/:organizationId/contacts-upload",
            element: <ContactsUpload />,
          },
        ],
      },

      // ONBOARDING
      {
        path: "onboarding",
        element: <OnboardingLayout />,
        children: [
          { path: "upload-crm", element: <UploadCRM /> },
          { path: "information", element: <OnboardingInformation /> },
          { path: "create-organization", element: <CreateOrganization /> },
          { path: "create-showroom", element: <CreateShowroom /> },
          { path: "showroom-structure", element: <ShowroomStructure /> },
          { path: "account-portfolio", element: <AccountPortfolio /> },
        ],
      },

      // BUYER + MISC
      {
        path: "buyer",
        element: <BuyerLayout />,
        children: [
          { path: "campaigns", element: <BuyerCampaigns /> },
          { path: "heatmap", element: <BuyerShowroomHeatmap /> },
        ],
      },

      // CRUISING
      {
        path: "cruising",
        element: <CruisingLayout />,
        children: [
          { index: true, element: <CruisingIndex /> },
          { path: "old", element: <CruisingOld /> },
          { path: "plan-my-trip", element: <PlanMyTrip /> },
        ],
      },

      // SUPPORT
      { path: "support", element: <SupportPage /> },

      { path: "*", element: <NotFound /> },
    ],
  },
];
